<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
  import { CartLayout } from '@storefront/components';
  import { useHttpClient } from '@othercommerce/foundation';

  interface Props {
    order: object;
    payment: object;
    statusUrl: string;
  }

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  const props = defineProps<Props>();

  const http = useHttpClient();
  const interval = ref() as Ref<number | undefined>;

  function refreshStatus() {
    http.get(props.statusUrl).then((res) => {
      if (res.redirect) {
        clearInterval(interval.value);
        http.get(res.redirect);
      }
    });
  }

  onMounted(() => {
    interval.value = setInterval(refreshStatus, 2000);
  });

  onBeforeUnmount(() => {
    clearInterval(interval.value);
  });
</script>
<template>
  <main id="main" class="order">
    <div class="order-processing">
      <SfTile class="order-processing__confirmation">
        <div class="order-processing__icon-wrapper">
          <SfSpinner />
        </div>
        <main class="order-processing__content">
          <header class="order-processing__heading-wrapper">
            <h1 class="order-processing__heading">{{ $t('SHOP.ORDER.HEADER.PAYMENT_PROCESSING') }}</h1>
            <p class="order-processing__request"> {{ $t('SHOP.ORDER.MESSAGE.PAYMENT_PROCESSING') }}</p>
          </header>
          <section class="order-processing__order-section">
            <p class="order-processing__order">
              <span>{{ $t('SHOP.ORDER.LABEL.ORDER_NUMBER') }}</span>
              <strong>{{ order.number }}</strong>
            </p>
          </section>
        </main>
      </SfTile>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .order-processing {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;
    text-align: center;

    &__confirmation {
      position: relative;
      width: 100%;
      max-width: 700px;
      margin-top: 3rem;
      text-wrap: pretty;
    }

    &__icon-wrapper {
      --sf-spinner-color: var(--sf-order-processing-icon-color);
      position: absolute;
      top: 0;
      left: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translate(-50%, -50%);
      height: 100px;
      width: 100px;
      font-size: 3rem;
      border-radius: 50%;
      background-color: var(--sf-order-processing-icon-background);
    }

    &__content,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__content {
      gap: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }

    &__heading {
      margin: 3rem 0 2rem;
    }

    &__order {
      font-size: 1.25rem;
    }

    &__order-id{
      word-break:break-word ;
    }

    &__request {
      font-size: 1.25rem;
      font-weight: var(--font-weight-semi);
      padding: 2rem;
    }

    &__security {
      font-size: 0.75rem;
    }
  }

  @include media.query(lg) {
    .order-processing {
      margin: 0;
      padding: 1rem 0;

      &__icon-wrapper {
        width: 70px;
        height: 70px;

        svg {
          font-size: 2rem;
        }
      }

      &__confirmation {
        box-shadow: var(--sf-tile-interactive-box-shadow);
        padding: 1rem 0;
      }

      &__header {
        margin-top: 1rem;
      }

      &__heading {
        font-size: 1.5rem;
        margin: 1rem 0;
      }
    }

  }
</style>
